/*
    This file is part of Universal Sequencer.
    Universal Sequencer is free software: you can redistribute it and/or modif
    it under the terms of the GNU General Public License as published by
    the Free Software Foundation, either version 3 of the License, or
    (at your option) any later version.

    Universal Sequencer is distributed in the hope that it will be useful,
    but WITHOUT ANY WARRANTY; without even the implied warranty of
    MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
    GNU General Public License for more details.

    You should have received a copy of the GNU General Public License
    along with this program.  If not, see <http://www.gnu.org/licenses/>.
*/

import Tone from 'tone';
import EasyPattern from 'pattern/EasyPattern.js';
import Mix from 'session/Mix.js';
import Resolution from 'engine/Resolution.js';
import StepData from 'pattern/StepData.js';
import StepBucket from 'pattern/StepBucket.js';
import Transformable from './groups/Transformable.js';
import {shouldMask, copyFromGroup, groupSteps} from './groups/stepGroups.js';
import {buildStepResolutionTree} from './StepResolutionTree.js';
import {setArrangementPatternStartStep} from 'actions/index.js';

const baseResolution = new Resolution(32, false);
const baseTripletResolution = new Resolution(32, true);
const oldDefaultResolution = new Resolution(16, false);
const defaultDuration = 1;
let id = 0;

class Pattern extends Transformable(class {}) {
    constructor(sequencerEngine, track) {
        super();
        this.steps = [];
        this.id = id++;
        this.sequencerEngine = sequencerEngine;
        this.store = sequencerEngine.store;
        this.track = track;

        this.frozenPatternPlaying = undefined;

        this.mix = new Mix(sequencerEngine);

        // we have a representation of buckets in both normal
        // 4/4 time and 3/4 (triplet) time. We dual write to these
        // lists with the same common StepData objects.
        this.stepBuckets = [];
        this.tripletStepBuckets = [];

        this.stepBucketsMatrix = {};
        this.tripletStepBucketsMatrix = {};

        // we don't want to needless recompute the tripletStepBuckets from
        // stepBuckets, so we try to cache it 
        this.transformBucketsIntoStepsCache = {};

        // stepFills is a list of booleans where it's true if
        // some Step has a duration that spills over into it
        this.stepFills = [];
        this.tripletStepFills = [];

        // Each pattern has it's own volume, panning and mutes
        this.volume = 0;
        this.panning = 0;
        this.muted = false;

        // initialize the pattern to all steps off
        this.initBlankPattern();
        this.color = undefined;
        this.loopsPlaying = {};

        if (!this.sequencerEngine.multiSequencerEngine) {
            this.updateStepResolutionTree();
        }

        this.needsSync = false;

        this._startStep = undefined;
        this.repeats = undefined;
    }

    set startStep(startStep) {
        if (startStep < 0) {
            return;
        }

        this._startStep = startStep;
        if (startStep === undefined) {
            return;
        }
        this.sequencerEngine.store.dispatch(
            setArrangementPatternStartStep(this.track.subSequence.id, this.id, startStep));
    }

    get startStep() {
        return this._startStep;
    }

    get step() {
        return this._startStep;
    }

    get duration() {
        return this.length();
    }

    get endStep() {
        return this._startStep + this.length();
    }

    updateStepResolutionTree() {
        this.stepResolutionTree = buildStepResolutionTree(this, false);
        this.tripletStepResolutionTree = buildStepResolutionTree(this, true);
    }

    getStepResolutionTree(isTriplet) {
        return isTriplet ? this.tripletStepResolutionTree : this.stepResolutionTree;
    }

    // gets the step buckets in a the range given by a stepNumber
    // and resolution. i.e. step 2 at 1/4 resolution is actually
    // several step buckets at 1/32 resolution
    getBucketsForStepAtResolution(stepNumber, resolution) {
        let numSteps = this.getRangeOfStepsAtResolution(resolution); 

        let stepBuckets = resolution.isTriplet ? this.tripletStepBuckets : this.stepBuckets;
        let transformedStepNumber = resolution.isTriplet ?
            baseTripletResolution.transformStepAtResolution(stepNumber, resolution) :
            baseResolution.transformStepAtResolution(stepNumber, resolution);
        transformedStepNumber = Math.floor(transformedStepNumber);
        let bucketsInRange = [];
        for (let i=transformedStepNumber; i < numSteps + transformedStepNumber; i++) {
            bucketsInRange.push(stepBuckets[i % stepBuckets.length]);
        }

        return bucketsInRange;
    }

    deleteStepNumber(stepNumber) {
        this.stepBuckets[stepNumber] = new StepBucket(this.track);
    }

    getRangeOfStepsAtResolution(resolution) {
        return resolution.isTriplet ?
            baseTripletResolution.numSteps() / resolution.numSteps() :
            baseResolution.numSteps() / resolution.numSteps();
    }

    getAllOnStepAtResolutionBeforePosition(stepNumber, resolution, position) {
        let buckets = this.getBucketsForStepAtResolution(stepNumber, resolution);
        return buckets[0].getAllOnStepsBefore(position);
    }

    getAllOnStepAtResolutionAtAnyPosition(stepNumber, resolution, position) {
        let numSteps = this.getRangeOfStepsAtResolution(resolution);
        let buckets = this.getBucketsForStepAtResolution(stepNumber, resolution);
        let steps = [];
        for (let i in buckets) {
            steps = steps.concat(buckets[i].getAllOnSteps(position));
        }

        return steps;
    }

    getStepAtResolution(stepNumber, resolution, checker, position=0) {
        let buckets = this.getBucketsForStepAtResolution(stepNumber, resolution);
        for (let i in buckets) {
            if (buckets[i] === undefined) {
                continue;
            }
            let stepData = buckets[i].getStep(position);
            if (stepData && stepData.getPolyphony().some(s => s.on && checker(s))) {
                return stepData;
            }
        }

        if (buckets.length === 0) {
            return undefined;
        }

        return undefined;
    }

    // returns a list of objects containing the stepData
    // object and the position (w/in the step bucket), for a
    // a given stepNumber at a resolution.
    // For example: "get me step 4 at 1/16th resolution" returns
    // all steps at step 4 at 1/16th resolution along w/ their
    // refined positions.
    getPolyphonicStepsAtResolution(stepNumber, resolution) {
        let stepBucket = undefined;
        let numSteps = resolution.isTriplet ?
            baseTripletResolution.numSteps() / resolution.numSteps() :
            baseResolution.numSteps() / resolution.numSteps();

        let steps = [];
        if (resolution.isTriplet) {
            let transformedStepNumber = baseTripletResolution
                .transformStepAtResolution(stepNumber, resolution);
            stepBucket = this.tripletStepBuckets[transformedStepNumber % this.tripletStepBuckets.length];


            for (let i=transformedStepNumber; i < numSteps + transformedStepNumber; i++) {
                let positionToStep = this.tripletStepBuckets[
                    i % this.tripletStepBuckets.length].steps;
                for (let position in positionToStep) {
                    let step = positionToStep[position];
                    let onSteps = step
                        .getPolyphony()
                        .filter(x => x.on)
                        .map(step => ({
                            position: position,
                            step: step
                        }));
                    steps = steps.concat(onSteps);
                }
            }
        } else {
            let transformedStepNumber =
                baseResolution.transformStepAtResolution(stepNumber, resolution);
            stepBucket = this.stepBuckets[transformedStepNumber % this.stepBuckets.length];
            for (let i=transformedStepNumber; i < numSteps + transformedStepNumber; i++) {
                let positionToStep = this.stepBuckets[
                    i % this.stepBuckets.length].steps;
                for (let position in positionToStep) {
                    let step = positionToStep[position];
                    let onSteps = step
                        .getPolyphony()
                        .filter(x => x.on)
                        .map(step => ({
                            position: parseFloat(position),
                            step: step
                        }));
                    steps = steps.concat(onSteps);
                }
            }
        }
        return steps;
    }

    setStepAtResolution(step, stepNumber, resolution, position=0) {
        if (stepNumber === undefined || isNaN(stepNumber)) {
            stepNumber = 0;
        }
        if (position === undefined || isNaN(position)) {
            position = 0;
        }

        if (resolution.isTriplet) {
            stepNumber = baseTripletResolution 
                .transformStepAtResolution2(stepNumber, resolution);
        } else {
            stepNumber = baseResolution
                .transformStepAtResolution2(stepNumber, resolution);
        }

        let wholeStepNumber = Math.floor(stepNumber);
        position += (stepNumber - wholeStepNumber );

        if (position >= 1) {
            let over = Math.floor(position);
            wholeStepNumber += over;
            position = position - over;
        }

        if (resolution.isTriplet) {
            this.tripletStepBuckets[wholeStepNumber % this.tripletStepBuckets.length]
                .setStepAtPosition(step, position);
        } else {
            let stepBucket = this.stepBuckets[wholeStepNumber % this.stepBuckets.length];
            stepBucket.setStepAtPosition(step, position);
        }
    }

    addNote(step, duration, transpose) {
        let stepData = new StepData();
        stepData.on = true;
        stepData.duration = duration;
        stepData.transpose = transpose;
        stepData.stepNumber = step;
        this.stepBuckets[step].setStep(0, stepData);
    }

    clearCache() {
        this.transformBucketsIntoStepsCache = {};
    }

    loadStepBuckets(stepBuckets) {
        if (stepBuckets) {
            this.stepBuckets = stepBuckets; 
        }
        this.tripletStepBuckets = baseTripletResolution
            .transformBucketsAtResolution(this.stepBuckets, baseResolution);
        this.updateStepResolutionTree();
        this.transformBucketsIntoStepsCache = {};
        this.syncMatrix();
    }

    syncBuckets(isTriplet) {
        isTriplet = isTriplet === undefined ? false : isTriplet;
        if (isTriplet) {
            this.stepBuckets = baseResolution
                .transformBucketsAtResolution(this.tripletStepBuckets, baseTripletResolution);
        } else {
            this.tripletStepBuckets = baseTripletResolution
                .transformBucketsAtResolution(this.stepBuckets, baseResolution);
        }
        this.syncMatrix();
    }

    syncBucketsAtStep(step, resolution) {
        let numSteps = this.getRangeOfStepsAtResolution(resolution); 
        let startStep = resolution.isTriplet ?
            baseTripletResolution.transformStepAtResolution(step, resolution) :
            baseResolution.transformStepAtResolution(step, resolution);
        startStep = Math.floor(startStep);
        let endStep = startStep + numSteps;

        if (resolution.isTriplet) {
            this.stepBuckets = baseResolution
                .transformBucketsAtResolution(
                    this.tripletStepBuckets,
                    baseTripletResolution,
                    startStep,
                    endStep,
                    this.stepBuckets );
        } else {
            this.tripletStepBuckets = baseTripletResolution
                .transformBucketsAtResolution(
                    this.stepBuckets,
                    baseResolution,
                    startStep,
                    endStep,
                    this.tripletStepBuckets);
        }

        this.syncMatrix();
    }

    syncMatrixBySteps(stepMapping, resolution) {
        this.needsSync = false;
        let stepBuckets = resolution.isTriplet ? this.tripletStepBuckets : this.stepBuckets;
        let stepBucketsMatrix = resolution.isTriplet ? this.tripletStepBucketsMatrix : this.stepBucketsMatrix;
        for (let x in stepMapping) {
            let y = stepMapping[x];
            x = baseResolution.transformStepAtResolution(x, resolution);
            y = baseResolution.transformStepAtResolution(y, resolution);
            let stepBucketX = stepBuckets[x];
            let stepBucketY = stepBuckets[y];

            if (stepBucketX && stepBucketX.anyOn()) {
                stepBucketsMatrix[x] = stepBucketX;
            }  else {
                delete stepBucketsMatrix[x];
            }
            if (stepBucketY && stepBucketY.anyOn()) {
                stepBucketsMatrix[y] = stepBucketY;
            }  else {
                delete stepBucketsMatrix[y];
            }
        }
    }

    syncMatrix() {
        this.stepBucketsMatrix = {};
        this.tripletStepBucketsMatrix = {};

        for (let i=0; i < this.stepBuckets.length; i++) {
            let stepBucket = this.stepBuckets[i];
            if (stepBucket.anyOn()) {
                this.stepBucketsMatrix[i] = stepBucket;
            } 
        }

        for (let i=0; i < this.tripletStepBuckets.length; i++) {
            let stepBucket = this.tripletStepBuckets[i];
            if (stepBucket.anyOn()) {
                this.tripletStepBucketsMatrix[i] = stepBucket;
            }
        }
        this.needsSync = false;
    }

    getBucket(step, resolution) {
        resolution = resolution || baseResolution;
        step = resolution.isTriplet ?
            baseTripletResolution.transformStepAtResolution2(step, resolution) :
            baseResolution.transformStepAtResolution2(step, resolution);
            
        let stepBuckets = this.isTransformed ? this.transformedStepBuckets : this.stepBuckets;
        return resolution.isTriplet ?
            this.tripletStepBuckets[step] :
            stepBuckets[step];
    }

    clear() {
        this.stepBuckets.map((bucket) => bucket.clear());
        this.syncBuckets();
        this.clearCache();
    }

    changeLength(x, sync=true) {
        x = Math.ceil(x);
        this.stepBuckets = this.stepBuckets.slice(0, x);
        for (let i=this.stepBuckets.length; i < x; i++) {
            this.stepBuckets.push(new StepBucket(this.track));
        }

        this.updateStepResolutionTree();
        this.clearCache();

        if (sync) {
            this.syncBuckets();
        }
    }

    length(resolution=baseResolution) {
        if (resolution === baseResolution) {
            return resolution.isTriplet ? this.tripletStepBuckets.length : this.stepBuckets.length;
        }

        let rawLength = resolution.isTriplet? this.tripletStepBuckets.length : this.stepBuckets.length;
        if (resolution.isTriplet) {
            return Math.ceil(resolution.transformStepAtResolution2(rawLength, baseTripletResolution));
        } else {
            return Math.ceil(resolution.transformStepAtResolution2(rawLength, baseResolution));
        }
    }

    initBlankPattern() {
        for (var i=0; i < baseResolution.numSteps(); i++) {
            this.stepBuckets.push(new StepBucket(this.track));
            this.stepFills.push(false);
        }

        if (this.sequencerEngine.multiSequencerEngine) {
            return;
        }
        for (var i=0; i < baseTripletResolution.numSteps(); i++) {
            this.tripletStepBuckets.push(new StepBucket(this.track, true));
            this.tripletStepFills.push(false);
        }
    }

    getFillsAtResolution(resolution) {
        if (resolution.isTriplet) {
            return resolution.transformList(this.tripletStepFills);
        } else {
            return resolution.transformList(this.stepFills);
        }
    }

    // responsible for returning a list of steps at the
    // given resolution. Basically, we store the original steps
    // at a resolution of 1/32, but sometimes the user wants
    // to see the steps in 1/4 resolution. This returns a list
    // of that length with those steps
    getStepsAtResolution(resolution=baseResolution) {
        let buckets = resolution.isTriplet ? this.tripletStepBuckets : this.stepBuckets;
        if (!(resolution in this.transformBucketsIntoStepsCache)) {
            let steps = resolution.transformBucketsIntoSteps(buckets);
            this.transformBucketsIntoStepsCache[resolution] = steps;
            return steps;
        }
        return this.transformBucketsIntoStepsCache[resolution];
    }

    // returns an "on" step if there are any, otherwise just returns
    // the first one
    getAnyStep(step) {
        for (let key in this.stepBuckets[step].steps) {
            let stepData = this.stepBuckets[step].steps[key];
            if (stepData.on) {
                return stepData;
            }
        }

        return this.stepBuckets[step].steps[0];
    }

    getBucketsAtResolution(resolution) {
        let buckets = resolution.isTriplet ? this.tripletStepBuckets : this.stepBuckets;
        return resolution.transformList(buckets);
    }

    setLength(length) {
        this.stepBuckets = [];
        for (let i=0; i < length; i++) {
            this.stepBuckets.push(new StepBucket(this.track));
        }

        this.syncBuckets(false);
    }

    doubleTrackLength() {
        var len = this.stepBuckets.length;
        for (var i=0; i < len; i++) {
            this.stepBuckets.push(new StepBucket(this.track));
            this.stepFills.push(false);
        }

        len = this.tripletStepBuckets.length;
        for (let i=0; i < len; i++) {
            this.tripletStepBuckets.push(new StepBucket(this.track, true));
            this.tripletStepFills.push(false);
        }

        this.clearCache();
        this.updateStepResolutionTree();
    }

    halfTrackLength() {
        let len = this.length() / 2;
        if (len <= 1) {
            return;
        }
        this.stepBuckets = this.stepBuckets.slice(0, len);
        this.stepFills = this.stepFills.slice(0, len);
        len = this.tripletStepBuckets.length / 2;
        this.tripletStepBuckets = this.tripletStepBuckets.slice(0, len);
        this.tripletStepFills = this.tripletStepFills.slice(0, len);
        this.clearCache();
        this.updateStepResolutionTree();
    }

    loadFromJson(json) {
        if (json.newVolume === undefined) {
            let v = json.volume + 40;
            this.volume = 128 * v / 60;
        } else {
            this.volume = json.volume;
        }
        this.panning = 'panning' in json ? json.panning : 0;
        this.muted = json.muted;
        this.loopsPlaying = 'loopsPlaying' in json ? json.loopsPlaying : {};
        if ('startStep' in json) {
            this.startStep = json.startStep;
        }
        if ('repeats' in json) {
            this.repeats = json.repeats;
        }

        this.frozenPatternPlaying = json.frozenPatternPlaying;
    }

    calculateFillsHelper(trackData, stepBuckets, resolution) {
        if (resolution.isTriplet) {
            this.tripletStepFills.fill(false);
        } else {
            this.stepFills.fill(false);
        }
        let numSteps = stepBuckets.length;
        for (var i=0; i < numSteps; i++) {
            var maxDuration =  -16;
            for (var key in stepBuckets[i].steps) {
                var step = stepBuckets[i].getStep(key);
                if (!step.on) {
                    continue;
                }
                var duration = step.duration == null ? trackData.duration :
                    step.duration;
                duration = Math.floor(duration) * 2;
                
                if (maxDuration < duration) {
                    maxDuration = duration;
                }
            }
            if (maxDuration < 1) {
                continue;
            }
            for (var j=0; j < maxDuration; j++) {
                var stepNumber = (i + j) % numSteps;
                if (resolution.isTriplet) {
                    this.tripletStepFills[stepNumber] = true;
                } else {
                    this.stepFills[stepNumber] = true;
                }
            }
        }
    }

    // calculates the "fills" which are basically a light shading to represent
    // the duration of a step. So if a step has duration 4, then the 3 1/16th steps
    // following the step will have a light shading, i.e. a fill
    calculateFills(trackData) {
        this.calculateFillsHelper(trackData, this.stepBuckets, baseResolution);
        this.calculateFillsHelper(trackData, this.tripletStepBuckets, baseTripletResolution);
    }

    getJson() {
        return {
            newVolume: true,
            automations: this.mix.getJson(),
            volume: this.volume,
            panning: this.panning,
            muted: this.muted,
            loopsPlaying: this.loopsPlaying,
            startStep: this.startStep,
            repeats: this.repeats,
            frozenPatternPlaying: this.frozenPatternPlaying
        };
    }

    getCopy(start=0, end=this.stepBuckets.length, track=this.track) {
        var pattern = new Pattern(this.sequencerEngine, track);
        pattern.frozenPatternPlaying = this.frozenPatternPlaying;
        pattern.stepBuckets = [];
        pattern.mix = this.mix;
        pattern.tripletStepBuckets = [];
        pattern.volume = this.volume;
        pattern.panning = this.panning;
        pattern.muted = this.muted;
        pattern.steps = [];
        pattern.loopsPlaying = {};
        Object.keys(this.loopsPlaying).map(key => pattern.loopsPlaying[key] = this.loopsPlaying[key]);

        let groups = this.getAllGroups();

        for (var i=start; i < end; i++) {
            let stepBucket = this.stepBuckets[i].getCopy(track);
            pattern.stepBuckets.push(stepBucket);
            pattern.stepFills.push(this.stepFills[i]);
        }

        let newSteps = pattern.getAllSteps();
        for (let group of groups) {
            let steps = newSteps.filter(x => x.group === group);
            let _group = groupSteps(steps, this.track, true);
            copyFromGroup(group, _group, track);
        }

        pattern.startStep = this.startStep === undefined ? undefined : this.startStep + start;

        pattern.syncBuckets();
        pattern.updateStepResolutionTree();
        return pattern;
    }

    chooseColor(color) {
        this.color = color;
    }

    getUpdateStepsAction(syncBuckets=false, isTriplet=false) {
        let resolution = this.store.getState().sequencer.displayResolution;
        let stepsLength = this.length(resolution);
        let steps = new Array(stepsLength).fill(false);

        let pianoRollSteps = {};
        let pianoRollPositions = {};
        let pianoRollDurations = {};

        // track fill mapping (based on transpose) that maps from step number
        // to a list of transposes that should be filled
        let trackFills = {};
        let groups = {};

        let stepToId = new Array(stepsLength);
        
        let easyPattern = this.getEasyPattern();

        if (syncBuckets) {
            easyPattern.update(isTriplet);
        }

        // stepBucketsMatrix is sparse-- it only has entries for ON steps
        // which is all we care about anyway 
        let matrix = resolution.isTriplet ?
            this.tripletStepBucketsMatrix :
            this.stepBucketsMatrix;
        let buckets = resolution.isTriplet ?
            this.tripletStepBuckets :
            this.stepBuckets;


        if (this.track.sequencerEngine.state.groups.stepsGroupMode &&
            this.isTransformed) {
            matrix = {};
            for (let key in this.transformedStepBuckets) {
                let bucket = this.transformedStepBuckets[key];
                if (bucket.anyOn()) {
                    let steps = bucket.getAllOnSteps();
                    let stepWithGroup = steps.find(x => x.group);
                    if (stepWithGroup && shouldMask(stepWithGroup)) {
                        continue;
                    }
                    matrix[key] = bucket;
                }
            }
        }

        let myBaseResolution = resolution.isTriplet ?
            new Resolution(32, true) :
            new Resolution(32, false);

        /* go through each "ON" step and determine:
         ** the transpose
         ** the position of the step (wrt to the resolution in a piano roll)
         ** the fills
         */
        for (let originalStepNumber in matrix) {
            // since matrix comes in 1/32th resolution, transform
            // into the given resolution
            let i = Math.floor(resolution.transformStepAtResolution2(originalStepNumber, myBaseResolution));
            steps[i] = true;
            let resolutionRatio = myBaseResolution.resolution / resolution.resolution;

            // a 1/32th stepNumber=1 is step=0 in 1/16ths w/ position 0.5
            let positionOffsetToAdd = (originalStepNumber - i * resolutionRatio)  / resolutionRatio;

            // map from transpose => list of positions of steps
            let positions = {};

            let polyphony = [];
            if (matrix[originalStepNumber] === undefined) {
                continue;
            }

            let durations = {};

            for (let position in matrix[originalStepNumber].steps) {
                let step = matrix[originalStepNumber].steps[position];
                let polyphonicSteps = step.getPolyphony();
                for (let j in polyphonicSteps) {
                    let polyphonicStep = polyphonicSteps[j];
                    if (!polyphonicStep.on) {
                        continue;
                    }
                    let transpose = Math.round(polyphonicStep.transpose);
                    let _position = parseFloat(position)/resolutionRatio + positionOffsetToAdd;
                    let myBaseResolution = new Resolution(16, false);
                    let duration = resolution.transformStepAtResolution2(polyphonicStep.duration, myBaseResolution);

                    if (! ( transpose in pianoRollSteps)) {
                        pianoRollSteps[transpose] = {};
                        pianoRollPositions[transpose] = {};
                        pianoRollDurations[transpose] = {};
                    }
                    polyphony.push(polyphonicStep);
                    let key = parseInt(i) + _position;
                    pianoRollSteps[transpose][key] = true;
                    pianoRollPositions[transpose][key] = 0; //_position;

                    pianoRollDurations[transpose][key] = duration;
                }
            }

            let step = matrix[originalStepNumber].getAllOnSteps()[0];
            stepToId[i] = step;
            groups[i] = step && step.group ? step.group.id : undefined;

            for (let j in polyphony) {
                let polyphonicStep = polyphony[j];
                if (!polyphonicStep.on) {
                    continue;
                }
                let transpose = polyphonicStep.transpose;
                if (transpose == null) {
                    transpose = this.trackData.transpose;
                }
                let duration = resolution.transformStepAtResolution2(
                    polyphonicStep.duration, new Resolution(16, false));

                for (let k=0; k < duration; k++) {
                    let stepNumberToFill = (parseInt(i) + parseInt(k)) % stepsLength; 
                    trackFills[parseInt(stepNumberToFill)] = true;
                }
            }
        }

        if (this.sequencerEngine.midi && 
            this.sequencerEngine.cursor.track === this.trackNumber) {
            this.sequencerEngine.midi.updateSteps(steps);
        }

        return {
            id: this.subSequence.isTrack ? this.subSequence.id : this.track.id,
            trackNumber: this.subSequence.isTrack ? this.subSequence.id : this.trackNumber,
            steps: steps,
            pianoRollSteps: pianoRollSteps,
            trackFills: trackFills,
            stepToId: stepToId,
            pianoRollPositions: pianoRollPositions,
            pianoRollDurations: pianoRollDurations,
            muted: this.track.muted,
            groups
        };
    }

    getTrackStepsAction() {
        let resolution = this.store.getState().sequencer.displayResolution;
        let stepsLength = this.length(resolution);
        let steps = new Array(stepsLength).fill(false);

        // stepBucketsMatrix is sparse-- it only has entries for ON steps
        // which is all we care about anyway 
        let matrix = resolution.isTriplet ?
            this.tripletStepBucketsMatrix :
            this.stepBucketsMatrix;

        let myBaseResolution = resolution.isTriplet ?
            new Resolution(32, true) :
            new Resolution(32, false);

        for (let originalStepNumber in matrix) {
            // since matrix comes in 1/32th resolution, transform
            // into the given resolution
            let i = Math.floor(resolution.transformStepAtResolution2(originalStepNumber, myBaseResolution));
            steps[i] = true;
        }

        return steps;
    }

    get trackPatternSteps() {
        let id = this.subSequence.isTrack ? this.subSequence.id : this.trackNumber;
        return this.sequencerEngine.state.tracks.trackPatternSteps[id] || {};
    }

    get subSequence() {
        return this.track.subSequence;
    }

    get trackNumber() {
        return this.track.trackNumber;
    }

    get trackData() {
        return this.track.trackData;
    }

    getEasyPattern() {
        return new EasyPattern(this, this.trackData);
    }

    split(step) {
        let relativeStep = step - this.startStep;
        let newLength = this.length() - relativeStep;

        let pattern = new Pattern(this.sequencerEngine, this.track);
        pattern.stepBuckets = this.stepBuckets.slice(relativeStep);
        this.stepBuckets = this.stepBuckets.slice(0, relativeStep);
        pattern.startStep = step;
        this.syncBuckets();
        pattern.syncBuckets();
        return pattern;
    }

    overlapsStep(step, inclusive=false) {
        if (inclusive) {
            return step >= this.startStep && step <= this.startStep + this.length();
        } else {
            return step >= this.startStep && step < this.startStep + this.length();
        }
    }

    addToStart(steps) {
        let stepsToAdd = [];
        for (let i=0; i < steps; i++) {
            stepsToAdd.push(new StepBucket(this.track));
        }
        this.stepBuckets = [... stepsToAdd, ... this.stepBuckets];
        this.syncBuckets();
    }

    cutFromStart(steps) {
        this.stepBuckets = this.stepBuckets.slice(steps);
        this.syncBuckets();
    }

    dispose() {
        this.stepBuckets = undefined;
        this.tripletStepBuckets = undefined;
        this.stepBucketsMatrix= undefined;
        this.tripletStepBucketsMatrix = undefined;
        this.stepFills = undefined;
        this.tripletStepFills = undefined;
        this.mix = undefined;
        this.track = undefined;
    }

    getAllSteps() {
        let steps = [];
        let stepBuckets = this.stepBuckets;
        for (let i in stepBuckets) {
            let stepBucket = stepBuckets[i];
            for (let position in stepBucket.steps) {
                let currentStepData = stepBucket.steps[position];
                steps = steps.concat(currentStepData.getPolyphony()
                    .filter(x => x.on));
            }
        }

        return steps;
    }
}

export default Pattern;

